import React, { useState } from 'react'
import { Modal, Button } from 'react-bootstrap'
import { formateDate, formateDateWithTime } from '../../../helpers/commonFunctions'
import QueuePanel from './QueuePanel'
import useQueue, { POLL_MS, showToast } from './useQueue'
import {
    KvRow,
    Section,
    JobNameBadge,
    decodeJwtPayload,
    expandJsonStrings,
    formatLabel,
    isEmpty,
    normalizeKey,
    renderKvGrid,
    renderScalar,
    useCompanies,
} from './jobDisplay'

// zatca and orders queue the same thing — an order payload with the caller's
// headers alongside it — so they share this screen. Each has its own component
// so either can diverge without touching the other.

// Both the job and the order it carries have a created-at; only the labels
// keep them apart, so the payload's are named for the order.
const ORDER_LABELS = {
    [normalizeKey('createdAt')]: 'Order Created At',
    [normalizeKey('zatcaDetailsAt')]: 'Order Zatca Details At',
}

const COLUMNS = [
    { label: 'S.No', width: '5%' },
    { label: 'Job ID', width: '12%' },
    { label: 'Job Name', width: '10%' },
    { label: 'Attempts', width: '10%' },
    { label: 'Company', width: '15%' },
    { label: 'Failed Reason', width: '18%' },
    { label: 'Data', width: '20%' },
    { label: 'Failed On', width: '10%' },
    { label: 'Queued At', width: '10%' },
]

const ORDER_FIELDS = [
    'operationType', 'orderStatus', 'isOrderStatusChanged', 'isSynced', 'isSyncedBackend',
    'outletName', 'outletLocation', 'outletId', 'brandId', 'companyId',
    'customerName', 'customerId', 'customerPhone', 'customerEmail', 'customerAddress',
    'invoiceUuid', 'invoiceCounterNumber', 'invoiceSerialNumber',
    'secondaryId', 'syncId', 'orderSource',
    'serviceType', 'tableName', 'tableNumber',
    'menuItemNames', 'menuItemsId', 'totalItemsCount',
    'createdAt', 'updatedAt', 'zatcaDetailsAt'
]
const PAYMENT_TENDER_FIELDS = [
    'tenderName', 'tenderId', 'paymentMethod', 'transactionId',
    'paymentStatus', 'isPaymentStatusChanged', 'paymentTime',
    'rrn', 'pan', 'nearpayPlatform'
]
const PAYMENT_PRICING_FIELDS = [
    'orderAmount',
    'discountPrice',
    'payableAmount',
    'subTotalPrice',
    'surchargePrice',
    'taxTotalPrice',
    'netAmount',
    'surchargeTaxAmount', 'grossAmount', 'taxAmount', 'taxPercentage', 'taxType',
    'finalPrice', 'totalPrice', 'cashAmount', 'changeAmount'
]
const PAYMENT_FIELDS = [...PAYMENT_TENDER_FIELDS, ...PAYMENT_PRICING_FIELDS]
const PAYMENT_TENDER_NORM = new Set(PAYMENT_TENDER_FIELDS.map(normalizeKey))
const PAYMENT_PRICING_NORM = new Set(PAYMENT_PRICING_FIELDS.map(normalizeKey))
const COUPON_FIELDS = [
    'couponCode', 'couponId', 'couponName', 'couponType',
    'couponDiscount', 'couponDiscountAmount', 'couponDiscountType',
    'isCouponApplied', 'discountType', 'discountValue'
]
const ORDER_MODE_ONLY_FIELDS = ['orderMode', 'orderModeId', 'orderModeName', 'orderModeSlug']
const AGGREGATOR_FIELDS = [
    'aggregatorExists', 'aggregatorId', 'aggregatorName', 'aggregatorOrder',
    'aggregatorOrderMode', 'aggregatorCommission', 'aggregatorExternalId',
    'aggregatorSku', 'aggregatorSlug'
]
const ORDER_MODE_FIELDS = [...ORDER_MODE_ONLY_FIELDS, ...AGGREGATOR_FIELDS]
const ORDER_MODE_ONLY_NORM = new Set(ORDER_MODE_ONLY_FIELDS.map(normalizeKey))
const DEVICE_FIELDS = ['deviceName', 'deviceId', 'posDeviceName', 'posDeviceId', 'lat', 'exp']
const ZATCA_FIELDS = ['zatcaDetails', 'zatcaDetailsRefund']
const HEADER_FIELDS = ['headers', 'decodedToken', 'originalToken', 'authorization']
const ITEMS_FIELDS = ['totalItems', 'recallItems']

const HIDE_FIELDS = ['orderModeConfiguration', 'operationType', 'outletName', 'outletLocation', 'updatedAt', 'menuItemNames', 'isSynced', 'isSyncedBackend', 'isOrderStatusChanged', 'orderSource', 'isPaymentStatusChanged']
const HIDE_NORM = new Set(HIDE_FIELDS.map(normalizeKey))
const SPECIAL_KEYS_NORM = new Set([...HEADER_FIELDS, ...ZATCA_FIELDS, ...ITEMS_FIELDS].map(normalizeKey))
const ORDER_NORM = new Set(ORDER_FIELDS.map(normalizeKey))
const PAYMENT_NORM = new Set(PAYMENT_FIELDS.map(normalizeKey))
const COUPON_NORM = new Set(COUPON_FIELDS.map(normalizeKey))
const ORDER_MODE_NORM = new Set(ORDER_MODE_FIELDS.map(normalizeKey))
const DEVICE_NORM = new Set(DEVICE_FIELDS.map(normalizeKey))

// Walks the data tree, collects scalar leaves (dedup'd by normalized key,
// empty values dropped) and pulls special keys into their own buckets.
const extractJobData = (root) => {
    const scalars = []
    const seen = new Set()
    const special = {}
    const walk = (val) => {
        if (val == null || typeof val !== 'object' || Array.isArray(val)) return
        for (const [k, v] of Object.entries(val)) {
            const nk = normalizeKey(k)
            if (HIDE_NORM.has(nk)) continue
            if (SPECIAL_KEYS_NORM.has(nk)) {
                if (!(nk in special)) special[nk] = { key: k, value: v }
                continue
            }
            if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
                walk(v)
            } else if (Array.isArray(v)) {
                // non-special arrays are not flattened
            } else {
                if (v == null || v === '') continue
                if (typeof v === 'string' && v.trim().toLowerCase() === 'n/a') continue
                if (!seen.has(nk)) {
                    seen.add(nk)
                    scalars.push([k, v])
                }
            }
        }
    }
    walk(root)
    return { scalars, special }
}

const OrderShapedQueue = ({ queue, title }) => {

    const queueState = useQueue(queue, { pollMs: POLL_MS })
    const companies = useCompanies()

    const [showJobModal, setShowJobModal] = useState(false)
    const [selectedJob, setSelectedJob] = useState(null)
    const [showZatcaModal, setShowZatcaModal] = useState(false)
    const [showHeadersModal, setShowHeadersModal] = useState(false)
    const [showItemsModal, setShowItemsModal] = useState(false)

    const openJobModal = (row) => {
        setSelectedJob(row)
        setShowJobModal(true)
    }

    const closeJobModal = () => {
        setShowJobModal(false)
        setSelectedJob(null)
        setShowZatcaModal(false)
        setShowHeadersModal(false)
        setShowItemsModal(false)
    }

    // A job carries { body, headers }: the caller's JWT in the headers names the
    // tenant (company_register_id), which maps to a company in the admin list.
    const jobRegisterId = (job) => {
        const data = job?.data || {}
        const body = data.body || data
        const headers = data.headers || {}
        const claims = decodeJwtPayload(headers.authorization || headers.Authorization)
        return body.company_register_id || claims?.company_register_id || null
    }

    const jobCompanyName = (job) => {
        const registerId = jobRegisterId(job)
        if (!registerId) return 'N/A'
        const company = companies.find(item => String(item?.company_id) === String(registerId))
        return company?.name || registerId
    }

    // Just the order body — the headers alongside it are only the caller's token.
    // Expanded so the encoded-JSON fields read as structures, not escaped lines.
    const jobPayload = (job) => {
        const payload = job?.data?.body ?? job?.data ?? null
        return payload ? expandJsonStrings(payload) : null
    }

    const copyPayload = (payload) => {
        navigator.clipboard?.writeText(JSON.stringify(payload ?? {}, null, 2))
            .then(() => showToast('success', 'Payload copied'))
            .catch(() => showToast('error', 'Could not copy payload'))
    }

    const renderMenuItems = (items) => {
        if (!Array.isArray(items) || items.length === 0) {
            return <span className="text-muted">No items</span>
        }
        return (
            <div className="d-flex flex-column" style={{ gap: 8 }}>
                {items.map((it, idx) => {
                    const name = it?.item_name || it?.name || `Item ${idx + 1}`
                    const nameAr = it?.item_name_ar || it?.name_ar
                    const sku = it?.sku
                    const qty = it?.quantity
                    const price = it?.final_price != null ? it.final_price : it?.price
                    const taxAmt = it?.tax_amount
                    const addOnsPrice = it?.addons_price
                    return (
                        <div key={idx} className="border rounded p-2 bg-white">
                            <div className="d-flex justify-content-between flex-wrap" style={{ gap: 8 }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 600 }}>{name}</div>
                                    {nameAr ? <div className="text-muted small">{nameAr}</div> : null}
                                    {sku ? <div className="small">SKU: <span className="text-muted">{sku}</span></div> : null}
                                </div>
                                <div className="text-end small" style={{ whiteSpace: 'nowrap' }}>
                                    {qty != null ? <div>Qty: <b>{qty}</b></div> : null}
                                    {price != null ? <div>Price: <b>{Number(price).toFixed(2)}</b></div> : null}
                                    {taxAmt != null && Number(taxAmt) ? <div className="text-muted">Tax: {Number(taxAmt).toFixed(2)}</div> : null}
                                    {addOnsPrice != null && Number(addOnsPrice) ? <div className="text-muted">Add-ons: {Number(addOnsPrice).toFixed(2)}</div> : null}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    // The panel wraps these in the <tr> and adds the select checkbox.
    const renderRow = (row, i) => (
        <>
            <td>{i + 1}</td>
            <td>{row.id || 'N/A'}</td>
            <td><JobNameBadge name={row.name} /></td>
            <td>{row.attemptsMade != null ? row.attemptsMade : (row.attempts != null ? row.attempts : 'N/A')}</td>
            <td>{jobCompanyName(row)}</td>
            <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '300px' }}>
                {row.failedReason
                    ? (String(row.failedReason).length > 120
                        ? String(row.failedReason).slice(0, 120) + '…'
                        : row.failedReason)
                    : 'N/A'}
            </td>
            <td>
                <button className="btn ripple btn-main-primary signbtn" title='View payload' onClick={() => openJobModal(row)}>
                    View
                </button>
            </td>
            <td>{row.failedOn ? formateDate(row.failedOn) : (row.finishedOn ? formateDate(row.finishedOn) : 'N/A')}</td>
            <td>{row.timestamp ? formateDateWithTime(row.timestamp) : 'N/A'}</td>
        </>
    )

    return (
        <>
            <QueuePanel
                queueState={queueState}
                title={title}
                pollMs={POLL_MS}
                columns={COLUMNS}
                renderRow={renderRow}
            />

            <Modal show={showJobModal} onHide={closeJobModal} size="lg" scrollable centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Failed Job
                        {selectedJob?.id ? <small className="text-muted ms-2">#{selectedJob.id}</small> : null}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '75vh', background: '#fafbff' }}>
                    {selectedJob ? (() => {
                        const { scalars, special } = extractJobData(selectedJob.data || {})
                        const orderEntries = scalars.filter(([k]) => ORDER_NORM.has(normalizeKey(k)))
                        const paymentEntries = scalars.filter(([k]) => PAYMENT_NORM.has(normalizeKey(k)))
                        const paymentTenderEntries = paymentEntries.filter(([k]) => PAYMENT_TENDER_NORM.has(normalizeKey(k)))
                        const pricingOrderNorm = PAYMENT_PRICING_FIELDS.map(normalizeKey)
                        const paymentPricingEntries = paymentEntries
                            .filter(([k]) => PAYMENT_PRICING_NORM.has(normalizeKey(k)))
                            .sort((a, b) => pricingOrderNorm.indexOf(normalizeKey(a[0])) - pricingOrderNorm.indexOf(normalizeKey(b[0])))
                        const couponEntries = scalars.filter(([k]) => COUPON_NORM.has(normalizeKey(k)))
                        const orderModeEntries = scalars.filter(([k]) => ORDER_MODE_NORM.has(normalizeKey(k)))
                        const orderModeOnlyEntries = orderModeEntries.filter(([k]) => ORDER_MODE_ONLY_NORM.has(normalizeKey(k)))
                        const aggregatorEntries = orderModeEntries.filter(([k]) => !ORDER_MODE_ONLY_NORM.has(normalizeKey(k)))
                        const deviceEntries = scalars.filter(([k]) => DEVICE_NORM.has(normalizeKey(k)))
                        const others = scalars.filter(([k]) => {
                            const nk = normalizeKey(k)
                            return !ORDER_NORM.has(nk) && !PAYMENT_NORM.has(nk)
                                && !COUPON_NORM.has(nk) && !ORDER_MODE_NORM.has(nk)
                                && !DEVICE_NORM.has(nk)
                        })
                        const totalItems = special[normalizeKey('totalItems')]?.value
                        const recallItems = special[normalizeKey('recallItems')]?.value
                        const hasItems = (Array.isArray(totalItems) && totalItems.length) || (Array.isArray(recallItems) && recallItems.length)
                        const hasZatca = ZATCA_FIELDS.some(f => special[normalizeKey(f)] && !isEmpty(special[normalizeKey(f)].value))
                        const hasHeaders = HEADER_FIELDS.some(f => special[normalizeKey(f)] && !isEmpty(special[normalizeKey(f)].value))
                        const itemsCount = (Array.isArray(totalItems) ? totalItems.length : 0) + (Array.isArray(recallItems) ? recallItems.length : 0)
                        return (
                            <>
                                <Section title="Job Info">
                                    <div className="row">
                                        <KvRow label="Job ID" value={renderScalar(selectedJob.id)} />
                                        <KvRow label="Company" value={renderScalar(jobCompanyName(selectedJob))} />
                                        <KvRow label="Name" value={renderScalar(selectedJob.name)} />
                                        <KvRow label="Attempts" value={renderScalar(selectedJob.attemptsMade != null ? selectedJob.attemptsMade : selectedJob.attempts)} />
                                        <KvRow label="Failed On" value={selectedJob.failedOn ? formateDate(selectedJob.failedOn) : (selectedJob.finishedOn ? formateDate(selectedJob.finishedOn) : '—')} />
                                        <KvRow label="Queued At" value={selectedJob.timestamp ? formateDateWithTime(selectedJob.timestamp) : '—'} full />
                                    </div>
                                </Section>

                                {selectedJob.failedReason ? (
                                    <Section title="Failed Reason">
                                        <div className="p-2 border rounded bg-light" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {selectedJob.failedReason}
                                        </div>
                                    </Section>
                                ) : null}

                                {(hasItems || hasZatca || hasHeaders) ? (
                                    <Section title="Diagnostics">
                                        <div className="d-flex flex-wrap gap-2">
                                            {hasItems ? (
                                                <button type="button" className="btn btn-outline-success btn-sm" onClick={() => setShowItemsModal(true)}>
                                                    <i className="fa fa-cutlery me-1" /> View Menu Items ({itemsCount})
                                                </button>
                                            ) : null}
                                            {hasZatca ? (
                                                <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setShowZatcaModal(true)}>
                                                    <i className="fa fa-file-text-o me-1" /> View Zatca Details
                                                </button>
                                            ) : null}
                                            {hasHeaders ? (
                                                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowHeadersModal(true)}>
                                                    <i className="fa fa-code me-1" /> View Headers & Token
                                                </button>
                                            ) : null}
                                        </div>
                                    </Section>
                                ) : null}

                                {orderEntries.length ? (
                                    <Section title="Order Details">
                                        {renderKvGrid(orderEntries, ['invoiceUuid', 'invoiceSerialNumber', 'syncId'], ORDER_LABELS)}
                                    </Section>
                                ) : null}

                                {orderModeEntries.length ? (
                                    <Section title="Order Mode & Aggregator Details">
                                        {orderModeOnlyEntries.length ? renderKvGrid(orderModeOnlyEntries) : null}
                                        {aggregatorEntries.length ? renderKvGrid(aggregatorEntries) : null}
                                    </Section>
                                ) : null}

                                {paymentEntries.length ? (
                                    <Section title="Payment Details">
                                        {paymentTenderEntries.length ? renderKvGrid(paymentTenderEntries) : null}
                                        {paymentPricingEntries.length ? renderKvGrid(paymentPricingEntries) : null}
                                    </Section>
                                ) : null}

                                {couponEntries.length ? (
                                    <Section title="Coupon Details">
                                        {renderKvGrid(couponEntries)}
                                    </Section>
                                ) : null}

                                {deviceEntries.length ? (
                                    <Section title="Device Details">
                                        {renderKvGrid(deviceEntries)}
                                    </Section>
                                ) : null}

                                {others.length ? (
                                    <Section title="Other Details">
                                        {renderKvGrid(others)}
                                    </Section>
                                ) : null}

                                <Section title="Payload">
                                    <div className='d-flex justify-content-end mb-2'>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => copyPayload(jobPayload(selectedJob))}
                                        >
                                            <i className="fa fa-clipboard me-1" /> Copy JSON
                                        </button>
                                    </div>
                                    <pre className='p-3 mb-0 border rounded' style={{
                                        background: '#f7f7fa',
                                        maxHeight: '360px',
                                        overflow: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontSize: '12px',
                                    }}>
                                        {jobPayload(selectedJob) ? JSON.stringify(jobPayload(selectedJob), null, 2) : 'N/A'}
                                    </pre>
                                </Section>
                            </>
                        )

                    })() : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeJobModal}>Close</Button>
                </Modal.Footer>
            </Modal>

            {(() => {
                const { special } = selectedJob ? extractJobData(selectedJob.data || {}) : { special: {} }
                const renderHeadersBucket = () => {
                    const entry = special[normalizeKey('headers')]
                    if (!entry) return null
                    const { key: k, value: v } = entry
                    if (isEmpty(v)) {
                        return (
                            <Section key={k} title={formatLabel(String(k))}>
                                <span className="text-muted">No data</span>
                            </Section>
                        )
                    }
                    if (v === null || typeof v !== 'object' || Array.isArray(v)) {
                        return (
                            <Section key={k} title={formatLabel(String(k))}>
                                {renderKvGrid([[k, v]])}
                            </Section>
                        )
                    }
                    let entries = Object.entries(v)
                    const hasOnlineToken = entries.some(([ek]) => normalizeKey(ek) === normalizeKey('onlineOrderApiAccessToken'))
                    const hasOnlineSecret = entries.some(([ek]) => normalizeKey(ek) === normalizeKey('onlineOrderApiSecretKey'))
                    const drop = new Set()
                    if (hasOnlineToken) drop.add(normalizeKey('authorization'))
                    if (hasOnlineSecret) drop.add(normalizeKey('secretKey'))
                    entries = entries.filter(([ek]) => !drop.has(normalizeKey(ek)))
                    const contextOrder = ['orderSource', 'isCashierPanel', 'currentApiUrl', 'onlineOrderApiAccessToken', 'authorization', 'onlineOrderApiSecretKey', 'secretKey']
                    const contextNorm = new Set(contextOrder.map(normalizeKey))
                    const ctxEntries = entries
                        .filter(([ek]) => contextNorm.has(normalizeKey(ek)))
                        .sort((a, b) => contextOrder.map(normalizeKey).indexOf(normalizeKey(a[0])) - contextOrder.map(normalizeKey).indexOf(normalizeKey(b[0])))
                    const headerEntries = entries.filter(([ek]) => !contextNorm.has(normalizeKey(ek)))
                    const labelMap = {
                        [normalizeKey('onlineOrderApiAccessToken')]: 'Token',
                        [normalizeKey('authorization')]: 'Token',
                        [normalizeKey('onlineOrderApiSecretKey')]: 'Secret Key',
                        [normalizeKey('secretKey')]: 'Secret Key',
                    }
                    return (
                        <React.Fragment key={k}>
                            {ctxEntries.length ? (
                                <Section title="Order Context">
                                    {renderKvGrid(ctxEntries, [], labelMap)}
                                </Section>
                            ) : null}
                            {headerEntries.length ? (
                                <Section title="Request Headers">
                                    {renderKvGrid(headerEntries)}
                                </Section>
                            ) : null}
                        </React.Fragment>
                    )
                }

                const renderSpecialBucket = (fieldList) => fieldList.map(f => {
                    if (normalizeKey(f) === normalizeKey('headers')) return renderHeadersBucket()
                    const entry = special[normalizeKey(f)]
                    if (!entry) return null
                    const { key: k, value: v } = entry
                    return (
                        <Section key={k} title={formatLabel(String(k))}>
                            {isEmpty(v) ? (
                                <span className="text-muted">No data</span>
                            ) : v !== null && typeof v === 'object' && !Array.isArray(v) ? (
                                renderKvGrid(Object.entries(v))
                            ) : (
                                renderKvGrid([[k, v]])
                            )}
                        </Section>
                    )
                })
                const totalItems = special[normalizeKey('totalItems')]?.value
                const recallItems = special[normalizeKey('recallItems')]?.value
                return (
                    <>
                        <Modal show={showZatcaModal} onHide={() => setShowZatcaModal(false)} size="lg" scrollable centered>
                            <Modal.Header closeButton>
                                <Modal.Title>Zatca Details</Modal.Title>
                            </Modal.Header>
                            <Modal.Body style={{ maxHeight: '75vh', background: '#fafbff' }}>
                                {renderSpecialBucket(ZATCA_FIELDS)}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setShowZatcaModal(false)}>Close</Button>
                            </Modal.Footer>
                        </Modal>

                        <Modal show={showHeadersModal} onHide={() => setShowHeadersModal(false)} size="lg" scrollable centered>
                            <Modal.Header closeButton>
                                <Modal.Title>Headers & Token</Modal.Title>
                            </Modal.Header>
                            <Modal.Body style={{ maxHeight: '75vh', background: '#fafbff' }}>
                                {renderSpecialBucket(HEADER_FIELDS)}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setShowHeadersModal(false)}>Close</Button>
                            </Modal.Footer>
                        </Modal>

                        <Modal show={showItemsModal} onHide={() => setShowItemsModal(false)} size="lg" scrollable centered>
                            <Modal.Header closeButton>
                                <Modal.Title>Menu Items</Modal.Title>
                            </Modal.Header>
                            <Modal.Body style={{ maxHeight: '75vh', background: '#fafbff' }}>
                                {Array.isArray(totalItems) && totalItems.length ? (
                                    <Section title={`Items (${totalItems.length})`}>
                                        {renderMenuItems(totalItems)}
                                    </Section>
                                ) : null}
                                {Array.isArray(recallItems) && recallItems.length ? (
                                    <Section title={`Recall Items (${recallItems.length})`}>
                                        {renderMenuItems(recallItems)}
                                    </Section>
                                ) : null}
                                {!(Array.isArray(totalItems) && totalItems.length) && !(Array.isArray(recallItems) && recallItems.length) ? (
                                    <span className="text-muted">No menu items</span>
                                ) : null}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={() => setShowItemsModal(false)}>Close</Button>
                            </Modal.Footer>
                        </Modal>
                    </>
                )

            })()}
        </>
    )
}

export default OrderShapedQueue
